import React, { useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Menu, X, BookOpen, Facebook, Instagram, Twitter, Linkedin } from 'lucide-react';
import VoiceChatbot from './VoiceChatbot';
import { NAV_LINKS } from '../constants';
import logo from '/Aziz_Edu_Logo_withou_bg.png';

export const Layout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  React.useEffect(() => {
    setIsMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <div className="min-h-screen flex flex-col bg-indigo-50 overflow-x-hidden">
      <nav className="sticky top-0 z-40 bg-white/95 backdrop-blur-md shadow-sm border-b border-indigo-200">
        <div className="container mx-auto px-4 lg:px-8 h-20 flex items-center justify-between max-w-7xl">
          <Link to="/" className="flex items-center gap-2 group">
             <img src={logo} alt="Aziz Edu University of Technology" className="h-12 sm:h-16 md:h-20 w-auto object-contain max-h-20" />
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((link) => {
              const isActive = location.pathname === link.path || (link.path !== '/' && location.pathname.startsWith(link.path));
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-semibold tracking-wide uppercase transition-colors hover:text-indigo-600 ${
                    isActive ? 'text-indigo-600 underline decoration-2 underline-offset-4' : 'text-gray-600'
                  }`}
                >
                  {link.name}
                </Link>
              );
            })}
          </div>

          <div className="flex items-center gap-4">
            <Link to="/programs" className="hidden sm:block bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white px-6 py-2.5 rounded-md font-medium text-sm transition-all shadow-md hover:shadow-lg hover:shadow-indigo-500/30">
              Browse Programs
            </Link>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-gray-600 hover:text-indigo-600"
            >
              {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="lg:hidden bg-white border-b border-indigo-200 absolute w-full left-0 top-20 shadow-lg py-4 px-6 flex flex-col gap-4 animate-in slide-in-from-top-5">
            {NAV_LINKS.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-lg font-medium py-2 border-b border-gray-50 ${
                    isActive ? 'text-indigo-600' : 'text-gray-600'
                  }`}
                >
                  {link.name}
                </Link>
              );
            })}
            <Link to="/programs" className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-3 rounded-md text-center font-bold mt-2">
              Browse Programs
            </Link>
          </div>
        )}
      </nav>

      <main className="flex-grow">
        {children}
      </main>

      <footer className="bg-gradient-to-br from-slate-900 to-indigo-950 text-white pt-16 pb-8">
        <div className="container mx-auto px-4 lg:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12 max-w-7xl">
           <div>
              <div className="mb-6">
                <img src={logo} alt="Aziz Edu University of Technology" className="h-12 sm:h-16 md:h-20 w-auto object-contain bg-white rounded-lg p-2 max-h-20" />
              </div>
              <p className="text-gray-300 leading-relaxed mb-6 text-sm">
                Leading computer science and technology university since 2010.
                Shaping Pakistan's tech industry leaders through innovation and excellence.
              </p>
              <div className="flex gap-4">
                 <a href="#" className="bg-white/10 p-2 rounded-full hover:bg-indigo-500 transition-colors"><Facebook size={18} /></a>
                 <a href="#" className="bg-white/10 p-2 rounded-full hover:bg-indigo-500 transition-colors"><Instagram size={18} /></a>
                 <a href="#" className="bg-white/10 p-2 rounded-full hover:bg-indigo-500 transition-colors"><Twitter size={18} /></a>
                 <a href="#" className="bg-white/10 p-2 rounded-full hover:bg-indigo-500 transition-colors"><Linkedin size={18} /></a>
              </div>
           </div>

           <div>
              <h4 className="font-sans text-lg font-bold mb-6 text-cyan-400">Quick Links</h4>
              <ul className="space-y-3 text-gray-300">
                <li><Link to="/programs" className="hover:text-cyan-400 transition-colors">Programs</Link></li>
                <li><Link to="/admissions" className="hover:text-cyan-400 transition-colors">Admissions</Link></li>
                <li><Link to="/about" className="hover:text-cyan-400 transition-colors">About Us</Link></li>
                <li><Link to="/contact" className="hover:text-cyan-400 transition-colors">Contact</Link></li>
                <li><Link to="/reviews" className="hover:text-cyan-400 transition-colors">Student Reviews</Link></li>
              </ul>
           </div>

           <div>
              <h4 className="font-sans text-lg font-bold mb-6 text-cyan-400">Programs</h4>
              <ul className="space-y-3 text-gray-300 text-sm">
                <li>BS Computer Science</li>
                <li>BS Software Engineering</li>
                <li>BS Artificial Intelligence</li>
                <li>BS Cybersecurity</li>
                <li>BS Data Science</li>
                <li>BS Cloud Computing</li>
              </ul>
           </div>

           <div>
              <h4 className="font-sans text-lg font-bold mb-6 text-cyan-400">Contact</h4>
              <ul className="space-y-3 text-gray-300 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-cyan-400 mt-1">📍</span>
                  <span>ST-2/A, Sector 15, Korangi Industrial Area</span>
                </li>
                <li>Karachi, Pakistan</li>
                <li className="flex items-center gap-2">
                  <span className="text-cyan-400">📞</span>
                  <span>(92-21) 3500-1234</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-cyan-400">✉️</span>
                  <span>info@azizedu.edu.pk</span>
                </li>
              </ul>
           </div>
        </div>

        <div className="border-t border-white/10 pt-8">
          <div className="container mx-auto px-4 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © 2026 Aziz Edu University of Technology. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm text-gray-400">
              <Link to="#" className="hover:text-cyan-400 transition-colors">Privacy Policy</Link>
              <Link to="#" className="hover:text-cyan-400 transition-colors">Terms of Service</Link>
              <Link to="#" className="hover:text-cyan-400 transition-colors">Cookie Policy</Link>
            </div>
          </div>
        </div>
      </footer>

      <VoiceChatbot />
    </div>
  );
};
